import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMessage } from '../context/MessageContext';
import { 
  fetchCase, 
  fetchCaseFiles, 
  fetchConversations, 
  createConversation, 
  fetchConversation, 
  sendMessage,
  createCase,
  uploadFile,
  fetchPrompts,
  deleteConversation
} from '../services/api';
import '../styles/CaseDetail.css';

function CaseDetail() {
  const { caseId } = useParams();
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { success, error: logError } = useMessage();
  
  const [caseData, setCaseData] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesHasMore, setFilesHasMore] = useState(false);
  const [filesLastId, setFilesLastId] = useState(null);
  const [filesLoading, setFilesLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationData, setConversationData] = useState(null);
  const [message, setMessage] = useState('');
  const [messageTemperature, setMessageTemperature] = useState(0.7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [newConversationTitle, setNewConversationTitle] = useState('');
  const [initialPrompt, setInitialPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [showUploadFileModal, setShowUploadFileModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const [fileUploadError, setFileUploadError] = useState(null);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [fileMetadata, setFileMetadata] = useState({
    filename: '',
    description: '',
    tags: []
  });
  
  // Prompts state
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState('');
  
  // We're now using the global message context instead of local state

  const messagesEndRef = useRef(null);
  
  // Scroll to the bottom of messages when they update
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // State for new matter creation
  const [isNewCase, setIsNewCase] = useState(false);
  const [newCaseData, setNewCaseData] = useState({
    title: '',
    description: '',
    status: 'New'
  });

  // Handle input change for new matter form
  const handleNewCaseInputChange = (e) => {
    const { name, value } = e.target;
    setNewCaseData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle new matter submission
  const handleCreateCase = async (e) => {
    e.preventDefault();
    
    if (!userProfile || !userProfile.teamId) {
      setError('User profile or team ID not found');
      return;
    }
    
    try {
      setLoading(true);
      console.log('Creating new matter:', newCaseData);
      console.log('With team ID:', userProfile.teamId);
      
      // Use the imported createCase function
      const createdCase = await createCase(userProfile.teamId, newCaseData);
      console.log('Matter created successfully:', createdCase);
      
      // Add success message to log
      success(`Matter "${newCaseData.title}" created successfully`);
      
      // Go to dashboard instead, like cancel button does
      goToDashboard();
    } catch (err) {
      console.error('Error creating matter:', err);
      const errorMessage = 'Failed to create matter: ' + (err.response?.data?.message || err.message);
      setError(errorMessage);
      logError(errorMessage);
      setLoading(false);
    }
  };

  const loadMoreFiles = async () => {
    if (!filesHasMore || filesLoading || !userProfile?.teamId) return;
    
    try {
      setFilesLoading(true);
      
      // Use the updated fetchCaseFiles function with lastId parameter
      const filesResponse = await fetchCaseFiles(
        userProfile.teamId, 
        caseId,
        10,
        false,
        filesLastId
      );
      
      // Append new files to existing files
      setFiles(prevFiles => [...prevFiles, ...(filesResponse.items || [])]);
      setFilesHasMore(filesResponse.hasMore || false);
      setFilesLastId(filesResponse.lastId || null);
      
      setFilesLoading(false);
    } catch (err) {
      console.error('Error loading more files:', err);
      setFilesLoading(false);
    }
  };

  useEffect(() => {
    if (!userProfile || !userProfile.teamId) {
      return;
    }

    // Check if we're creating a new matter
    if (caseId === 'new') {
      setIsNewCase(true);
      setLoading(false);
      return;
    }

    // If it's not a new matter, load existing matter data
    const loadCaseData = async () => {
      try {
        setLoading(true);
        const [caseResponse, filesResponse, conversationsResponse] = await Promise.all([
          fetchCase(userProfile.teamId, caseId),
          fetchCaseFiles(userProfile.teamId, caseId),
          fetchConversations(userProfile.teamId, caseId)
        ]);

        setCaseData(caseResponse);
        
        // Handle files with pagination support
        setFiles(filesResponse.items || []);
        setFilesHasMore(filesResponse.hasMore || false);
        setFilesLastId(filesResponse.lastId || null);
        
        setConversations(conversationsResponse.items || []);
        
        setLoading(false);
      } catch (err) {
        console.error('Error loading matter data:', err);
        setError('Failed to load matter data');
        setLoading(false);
      }
    };

    loadCaseData();
  }, [userProfile, caseId]);

  useEffect(() => {
    if (selectedConversation && userProfile?.teamId) {
      const loadConversation = async () => {
        try {
          const data = await fetchConversation(
            userProfile.teamId, 
            caseId, 
            selectedConversation
          );
          setConversationData(data);
        } catch (err) {
          console.error('Error loading conversation:', err);
          setError('Failed to load conversation');
        }
      };

      loadConversation();
    } else {
      setConversationData(null);
    }
  }, [selectedConversation, userProfile, caseId]);

  useEffect(() => {
    scrollToBottom();
  }, [conversationData]);
  
  // Load prompts when component mounts
  useEffect(() => {
    if (!userProfile?.teamId) return;
    
    const loadPrompts = async () => {
      try {
        const promptsData = await fetchPrompts(userProfile.teamId);
        console.log('Loaded prompts:', promptsData);
        
        // Check if prompts have template field
        if (Array.isArray(promptsData) && promptsData.length > 0) {
          console.log('First prompt structure:', promptsData[0]);
          console.log('First prompt has template field:', 'template' in promptsData[0]);
          console.log('Available fields in first prompt:', Object.keys(promptsData[0]));
        }
        
        setPrompts(promptsData);
      } catch (err) {
        console.error('Error loading prompts:', err);
      }
    };
    
    loadPrompts();
  }, [userProfile?.teamId]);

  const handleFileSelection = (fileId) => {
    if (selectedFiles.includes(fileId)) {
      setSelectedFiles(selectedFiles.filter(id => id !== fileId));
    } else {
      setSelectedFiles([...selectedFiles, fileId]);
    }
  };

  const handleCreateConversation = async () => {
    if (!userProfile?.teamId || !caseId) return;

    try {
      setLoading(true);
      const title = newConversationTitle || `Conversation ${conversations.length + 1}`;
      console.log('Creating conversation with title:', title);
      console.log('Initial prompt:', initialPrompt || '(none)');
      console.log('Selected files:', selectedFiles);
      
      const newConversation = await createConversation(
        userProfile.teamId,
        caseId,
        title,
        selectedFiles,
        initialPrompt, // Pass the initial prompt to the API
        selectedModel, // Pass the selected AI model
        messageTemperature // Pass the temperature setting
      );
      
      console.log(`Creating conversation with model: ${selectedModel}`);

      setConversations([...conversations, newConversation]);
      setSelectedConversation(newConversation.id);
      setNewConversationTitle('');
      setInitialPrompt(''); // Reset the initial prompt
      setSelectedModel(''); // Reset the model selection
      setSelectedPromptId(''); // Reset the selected prompt
      setSelectedFiles([]);
      setShowNewConversationModal(false);
      success(`Conversation "${title}" created successfully`);
      setLoading(false);
    } catch (err) {
      console.error('Error creating conversation:', err);
      console.error('Error details:', {
        status: err.response?.status,
        data: err.response?.data,
        message: err.message
      });
      const errorMessage = 'Failed to create conversation: ' + (err.response?.data?.message || err.message);
      setError(errorMessage);
      logError(errorMessage);
      // Close the modal even on error
      setShowNewConversationModal(false);
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!message.trim() || !selectedConversation) return;

    try {
      setLoading(true); // Set global loading state
      // Send the message and get the response data
      const data = await sendMessage(
        userProfile.teamId,
        caseId,
        selectedConversation,
        message,
        messageTemperature,
        "gemini-2.0-flash"
      );

      console.log('Message response data:', data);
        
      // Instead of fetching the whole conversation again, we can update locally with the received response
      if (conversationData && Array.isArray(conversationData.messages)) {
        // Create a copy of the current conversation data
        const updatedData = { ...conversationData };
        
        // Add the user message and AI response to messages
        const timestamp = new Date().toISOString();
        
        // Add user message
        updatedData.messages.push({
          content: message,
          role: 'user',
          position: data.userMessagePosition,
          timestamp: timestamp
        });
        
        // Add assistant message with the response from the API
        updatedData.messages.push({
          content: data.response,
          role: 'assistant',
          position: data.assistantMessagePosition,
          timestamp: timestamp
        });
        
        // Update the conversation data state
        setConversationData(updatedData);
      } else {
        // If we don't have conversation data structure yet, fetch the whole conversation
        const updatedConversation = await fetchConversation(
          userProfile.teamId, 
          caseId, 
          selectedConversation
        );
        
        setConversationData(updatedConversation);
      }
      setMessage('');
      success('Message sent successfully');
      setLoading(false); // Clear loading state
    } catch (err) {
      console.error('Error sending message:', err);
      const errorMessage = 'Failed to send message: ' + (err.response?.data?.message || err.message);
      setError(errorMessage);
      logError(errorMessage);
      setLoading(false); // Clear loading state on error
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const handleDeleteConversation = async (conversationId) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) {
      return;
    }
    
    try {
      setLoading(true); // Set global loading state
      await deleteConversation(userProfile.teamId, caseId, conversationId);
      
      // Remove the deleted conversation from the state
      setConversations(conversations.filter(convo => convo.id !== conversationId));
      
      // If the deleted conversation was selected, clear the selection
      if (selectedConversation === conversationId) {
        setSelectedConversation(null);
        setConversationData(null);
      }
      success('Conversation deleted successfully');
      setLoading(false); // Clear loading state
    } catch (err) {
      console.error('Error deleting conversation:', err);
      const errorMessage = 'Failed to delete conversation: ' + (err.response?.data?.message || err.message);
      setError(errorMessage);
      logError(errorMessage);
      setLoading(false); // Clear loading state on error
    }
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };
  
  const handlePromptSelection = (e) => {
    const promptId = e.target.value;
    setSelectedPromptId(promptId);
    
    if (promptId) {
      // Find the selected prompt
      const selectedPrompt = prompts.find(prompt => prompt.id === promptId);
      if (selectedPrompt) {
        console.log('Selected prompt:', selectedPrompt);
        console.log('Prompt template field:', selectedPrompt.template);
        
        // Populate the initial prompt with the prompt's template
        setInitialPrompt(selectedPrompt.template || selectedPrompt.content || selectedPrompt.promptText || selectedPrompt.prompt_text || '');
        
        // If prompt has a title, use it as the conversation title (unless user already entered one)
        if (selectedPrompt.title && !newConversationTitle) {
          setNewConversationTitle(selectedPrompt.title);
        }
      }
    } else {
      // If "Select a prompt" option is chosen, clear the initial prompt
      setInitialPrompt('');
    }
  };
  
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFileToUpload(file);
      
      // Initialize metadata with filename from the selected file
      setFileMetadata(prev => ({
        ...prev,
        filename: file.name
      }));
    }
  };
  
  const handleMetadataChange = (e) => {
    const { name, value } = e.target;
    setFileMetadata(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleTagsChange = (e) => {
    // Convert comma-separated string to array of trimmed tags
    const tagsArray = e.target.value
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag !== '');
    
    setFileMetadata(prev => ({
      ...prev,
      tags: tagsArray
    }));
  };
  
  const handleFileUpload = async () => {
    if (!fileToUpload || !userProfile?.teamId) return;
    
    try {
      setFileUploadLoading(true);
      setFileUploadError(null);
      setUploadProgress(0);
      setLoading(true); // Set global loading state
      
      // Upload the file with metadata
      const uploadedFile = await uploadFile(
        userProfile.teamId,
        caseId,
        fileToUpload,
        fileMetadata
      );
      
      // Update the files list with the new file
      setFiles(prevFiles => [uploadedFile, ...prevFiles]);
      
      // Reset state
      setFileToUpload(null);
      setFileMetadata({
        filename: '',
        description: '',
        tags: []
      });
      setShowUploadFileModal(false);
      setUploadProgress(100);
      success(`File "${uploadedFile.filename}" uploaded successfully`);
      
    } catch (err) {
      console.error('Error uploading file:', err);
      const errorMessage = 'Failed to upload file: ' + (err.response?.data?.message || err.message);
      setFileUploadError(errorMessage);
      logError(errorMessage);
      // Close the modal even on error
      setShowUploadFileModal(false);
    } finally {
      setFileUploadLoading(false);
      setLoading(false); // Clear global loading state
    }
  };

  return (
    <div className="matter-detail-container">
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Processing...</p>
          </div>
        </div>
      )}
      <header className="matter-detail-header">
        <div className="header-left">
          <button className="back-button" onClick={goToDashboard}>
            &larr; Back to matters
          </button>
          <h1>{isNewCase ? 'Create New Matter' : caseData?.title || 'Loading...'}</h1>
        </div>
        {!isNewCase && (
          <div className="matter-status">
            {caseData?.status || ''}
          </div>
        )}
      </header>

      {loading ? (
        <div className="loading">Loading matter data...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : isNewCase ? (
        // Form for creating a new matter
        <div className="matter-form-container">
          <form onSubmit={handleCreateCase} className="matter-form">
            <div className="form-group">
              <label htmlFor="title">Title</label>
              <input
                type="text"
                id="title"
                name="title"
                value={newCaseData.title}
                onChange={handleNewCaseInputChange}
                required
                placeholder="Enter matter title"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={newCaseData.description}
                onChange={handleNewCaseInputChange}
                rows={4}
                placeholder="Enter matter description"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={newCaseData.status}
                onChange={handleNewCaseInputChange}
              >
                <option value="New">New</option>
                <option value="In Progress">In Progress</option>
                <option value="Pending">Pending</option>
                <option value="Completed">Completed</option>
              </select>
            </div>
            
            <div className="form-actions">
              <button type="button" onClick={goToDashboard} className="cancel-button" disabled={loading}>
                Cancel
              </button>
              <button type="submit" className="create-button" disabled={loading}>
                {loading ? 'Creating...' : 'Create Matter'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="matter-content">
          <div className="sidebar">
            <div className="sidebar-section">
              <div className="section-header">
                <h3>Files</h3>
                <button 
                  className="new-button"
                  onClick={() => setShowUploadFileModal(true)}
                >
                  Upload
                </button>
              </div>
              <div className="file-list">
                {files.length === 0 ? (
                  <div className="no-items">No files</div>
                ) : (
                  <>
                    {files.map(file => (
                      <div 
                        key={file.id} 
                        className={`file-item ${selectedFiles.includes(file.id) ? 'selected' : ''}`}
                        onClick={() => handleFileSelection(file.id)}
                      >
                        <div className="file-details">
                          <span className="file-name">{file.filename}</span>
                          <span className="file-size">{(file.size / 1024).toFixed(1)} KB</span>
                          {file.description && <span className="file-description">{file.description}</span>}
                          {file.tags && file.tags.length > 0 && (
                            <div className="file-tags">
                              {file.tags.map((tag, index) => (
                                <span key={index} className="file-tag">{typeof tag === 'string' ? tag.replace(/[\[\]"]/g, '') : tag}</span>
                              ))}
                            </div>
                          )}
                          <span className="file-date">{new Date(file.createdAt || file.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                    
                    {filesHasMore && (
                      <div className="load-more-container">
                        <button 
                          className="load-more-button" 
                          onClick={loadMoreFiles}
                          disabled={filesLoading}
                        >
                          {filesLoading ? 'Loading...' : 'Load More'}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="sidebar-section">
              <div className="section-header">
                <h3>Conversations</h3>
                <button 
                  className="new-button"
                  onClick={() => {
                    setNewConversationTitle('');
                    setInitialPrompt('');
                    setSelectedModel('');
                    setSelectedPromptId('');
                    setShowNewConversationModal(true);
                  }}
                >
                  New
                </button>
              </div>
              <div className="conversation-list">
                {conversations.length === 0 ? (
                  <div className="no-items">No conversations</div>
                ) : (
                  conversations.map(convo => (
                    <div 
                      key={convo.id} 
                      className={`conversation-item ${selectedConversation === convo.id ? 'selected' : ''}`}
                      onClick={() => setSelectedConversation(convo.id)}
                    >
                      <div className="conversation-content">
                        <span className="conversation-title">{convo.title}</span>
                        <span className="conversation-date">
                          {new Date(convo.updatedAt || convo.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                      <button 
                        className="delete-conversation-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteConversation(convo.id);
                        }}
                        title="Delete conversation"
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="chat-area">
            {!selectedConversation ? (
              <div className="select-conversation-prompt">
                <h3>Select a conversation or create a new one</h3>
                <button 
                  className="create-conversation-button"
                  onClick={() => {
                    setNewConversationTitle('');
                    setInitialPrompt('');
                    setSelectedModel('');
                    setSelectedPromptId('');
                    setShowNewConversationModal(true);
                  }}
                >
                  Create New Conversation
                </button>
              </div>
            ) : (
              <>
                <div className="messages-container">
                  {conversationData?.messages && conversationData.messages.length > 0 ? (
                    conversationData.messages.map((msg, index) => (
                      <div 
                        key={index} 
                        className={`message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
                      >
                        <div className="message-header">
                          <span className="message-sender">{msg.role === 'user' ? 'You' : 'JurisAI'}</span>
                          <span className="message-time">
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="message-content">{msg.content}</div>
                      </div>
                    ))
                  ) : (
                    <div className="no-messages">
                      No messages yet. Start the conversation!
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="message-input-container">
                  <div className="message-controls">
                    <textarea
                      className="message-input"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type your user prompt here..."
                      rows={3}
                    />
                    <div className="message-options">
                      <div className="temperature-control">
                        <label htmlFor="messageTemperature">Temperature: {messageTemperature}</label>
                        <input
                          id="messageTemperature"
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={messageTemperature}
                          onChange={(e) => setMessageTemperature(parseFloat(e.target.value))}
                        />
                      </div>
                    </div>
                  </div>
                  <button 
                    className="send-button"
                    onClick={handleSendMessage}
                    disabled={!message.trim() || loading}
                  >
                    {loading ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showNewConversationModal && (
        <div className="modal-overlay">
          <div className="conversation-modal">
            <h2>Create New Conversation</h2>
            
            <div className="form-group">
              <label htmlFor="conversationTitle">Title</label>
              <input 
                type="text" 
                id="conversationTitle"
                value={newConversationTitle}
                onChange={(e) => setNewConversationTitle(e.target.value)}
                placeholder="Enter conversation title"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="promptSelect">Select a Prompt Template (Optional)</label>
              <select
                id="promptSelect"
                value={selectedPromptId}
                onChange={handlePromptSelection}
                className="prompt-select"
              >
                <option value="">Select a prompt...</option>
                {prompts.map(prompt => (
                  <option key={prompt.id} value={prompt.id}>
                    {prompt.title || prompt.name || `Prompt #${prompt.id}`}
                  </option>
                ))}
              </select>
              <div className="helper-text">Choose a pre-defined prompt template or write your own below</div>
            </div>
            
            <div className="form-group">
              <label htmlFor="initialPrompt">Initial User Prompt</label>
              <textarea 
                id="initialPrompt"
                value={initialPrompt}
                onChange={(e) => setInitialPrompt(e.target.value)}
                placeholder="Type your initial prompt here (optional)"
                rows={4}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="aiModel">AI Model (Optional)</label>
              <select
                id="aiModel"
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="model-select"
              >
                <option value="">Default</option>
                <option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
               
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="conversationTemperature">Temperature: {messageTemperature}</label>
              <input
                id="conversationTemperature"
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={messageTemperature}
                onChange={(e) => setMessageTemperature(parseFloat(e.target.value))}
              />
              <div className="helper-text">Controls randomness: 0 is more focused, 1 is more creative</div>
            </div>
            
            <div className="form-group">
              <label>Selected Files ({selectedFiles.length})</label>
              <div className="selected-files-list">
                {selectedFiles.length > 0 ? (
                  selectedFiles.map(fileId => {
                    const file = files.find(f => f.id === fileId);
                    return (
                      <div key={fileId} className="selected-file-item">
                        <span>{file ? file.filename : fileId}</span>
                        <button 
                          className="remove-file-button"
                          onClick={() => handleFileSelection(fileId)}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })
                ) : (
                  <div className="no-files-selected">No files selected</div>
                )}
              </div>
              <div className="file-selection-helper">
                Select files from the sidebar by clicking on them
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => setShowNewConversationModal(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="create-button"
                onClick={handleCreateConversation}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      
      {showUploadFileModal && (
        <div className="modal-overlay">
          <div className="file-upload-modal">
            <h2>Upload File</h2>
            
            {fileUploadError && (
              <div className="error-message">{fileUploadError}</div>
            )}
            
            <div className="form-group">
              <label htmlFor="fileInput">Select File</label>
              <input 
                type="file" 
                id="fileInput"
                onChange={handleFileInputChange}
                className="file-input"
              />
              {fileToUpload && (
                <div className="selected-file-info">
                  <span className="file-name">{fileToUpload.name}</span>
                  <span className="file-size">({(fileToUpload.size / 1024).toFixed(1)} KB)</span>
                </div>
              )}
            </div>
            
            <div className="form-group">
              <label htmlFor="filename">Custom Filename (Optional)</label>
              <input 
                type="text" 
                id="filename"
                name="filename"
                value={fileMetadata.filename}
                onChange={handleMetadataChange}
                placeholder="Enter custom filename"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description (Optional)</label>
              <textarea 
                id="description"
                name="description"
                value={fileMetadata.description}
                onChange={handleMetadataChange}
                placeholder="Enter file description"
                rows={3}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="tags">Tags (Optional, comma separated)</label>
              <input 
                type="text" 
                id="tags"
                value={fileMetadata.tags.join(', ')}
                onChange={handleTagsChange}
                placeholder="tag1, tag2, tag3"
              />
            </div>
            
            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="upload-progress">
                <div 
                  className="upload-progress-bar" 
                  style={{ width: `${uploadProgress}%` }}
                ></div>
                <span className="upload-progress-text">{uploadProgress}%</span>
              </div>
            )}
            
            <div className="modal-actions">
              <button 
                type="button" 
                className="cancel-button"
                onClick={() => {
                  setShowUploadFileModal(false);
                  setFileToUpload(null);
                  setFileMetadata({
                    filename: '',
                    description: '',
                    tags: []
                  });
                  setFileUploadError(null);
                }}
                disabled={fileUploadLoading || loading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="create-button"
                onClick={handleFileUpload}
                disabled={!fileToUpload || fileUploadLoading || loading}
              >
                {fileUploadLoading || loading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CaseDetail;